<!--
  The toast at the bottom of the page: the result of the member's own click.
  It shows `flash[:toast]` and any toast the page fires with `showToast`.
  With the sidebar on (`showsAllFlashes`) it also shows `notice` and `alert`,
  which the top-bar layout shows as bars (AlertsContainer). A success goes away after 5 seconds;
  a refusal, or a toast with an action such as Undo, stays longer. It stays
  while the mouse is on it.

  The layout persists between visits, so it watches the flash object, which
  is new on each visit: the same message twice in a row shows twice. It is not
  teleported: inside the layout, shell.css lifts it above the phone tab bar
  and centres it on the page column, not on the window.
-->
<template>
  <Transition name="flash-toast" mode="out-in">
    <div
      v-if="toast"
      :key="toast.id"
      :role="toast.tone === 'error' ? 'alert' : 'status'"
      class="gm-toast gm-above-tabbar fixed bottom-6 z-[1100] flex items-center gap-2 rounded-xl bg-neutral-900 py-2 pr-2 pl-4 text-sm text-white shadow-lg"
      @mouseenter="holdToast"
      @mouseleave="releaseToast"
    >
      <CircleAlertIcon
        v-if="toast.tone === 'error'"
        class="text-error-300 size-4 shrink-0"
        aria-hidden="true"
      />
      <CheckIcon
        v-else
        class="text-success-300 size-4 shrink-0"
        aria-hidden="true"
      />
      <span class="py-1">{{ toast.text }}</span>
      <button
        v-if="toast.action"
        type="button"
        class="ml-1 shrink-0 rounded-lg px-2.5 py-1 font-semibold text-white underline underline-offset-2 hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:outline-none"
        @click="runAction"
      >
        {{ toast.action.label }}
      </button>
      <button
        type="button"
        class="grid size-7 shrink-0 place-items-center rounded-lg text-white/60 hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:outline-none"
        @click="dismissToast"
      >
        <span class="sr-only">Close</span>
        <CloseIcon class="size-4" aria-hidden="true" />
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { usePage } from '@inertiajs/vue3'
import { onBeforeUnmount, onMounted, watch } from 'vue'
import CheckIcon from '~icons/lucide/check'
import CircleAlertIcon from '~icons/lucide/circle-alert'
import CloseIcon from '~icons/lucide/x'
import { useToast } from '@/shared/composables/useToast'

type Flash = {
  toast?: string | null
  success?: string | null
  warning?: string | null
}

const props = defineProps<{ showsAllFlashes?: boolean }>()

const page = usePage<{ shared?: { flash?: Flash } }>()
const { toast, showToast, dismissToast, holdToast, releaseToast } = useToast()

const show = (flash?: Flash) => {
  if (!flash) return
  if (flash.toast) return showToast({ text: flash.toast })
  if (!props.showsAllFlashes) return
  // `alert:` on a redirect is a refusal; `notice:` is a success.
  if (flash.warning) return showToast({ text: flash.warning, tone: 'error' })
  if (flash.success) showToast({ text: flash.success })
}

const runAction = () => {
  const action = toast.value?.action
  dismissToast()
  action?.run()
}

// Client only, so a server render never starts the timer.
onMounted(() => {
  show(page.props.shared?.flash)
  watch(() => page.props.shared?.flash, show)
})

onBeforeUnmount(dismissToast)
</script>

<style scoped>
/* Centred on the page column: the sidebar takes --gm-sidebar-w on the left
   (shell.css; 0 with no sidebar and on a phone). */
.gm-toast {
  left: calc(50% + var(--gm-sidebar-w, 0px) / 2);
  translate: -50% 0;
  /* A fixed box at left: 50% shrinks to half the page. This keeps one line
     until the text is as wide as the page column. */
  width: max-content;
  max-width: calc(100% - var(--gm-sidebar-w, 0px) - 2rem);
}

.flash-toast-enter-active,
.flash-toast-leave-active {
  transition:
    opacity 200ms ease,
    transform 200ms ease;
}

.flash-toast-enter-from,
.flash-toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
